// GA4 server-side event helper.
// Reads the GA4 client_id and session_id from first-party cookies and
// forwards events to the `track-signup-mp` Supabase Edge Function, which
// relays to GA4 via the Measurement Protocol.
//
// Allowed event_name values (must match ALLOWED_EVENTS in the edge function):
//   sign_up, signup_view, signup_start, signup_submit, signup_field_error,
//   first_login, cta_click

import { supabase } from "@/integrations/supabase/client";
import { isProdHost } from "@/lib/analyticsHost";
import { hasStoredAnalyticsConsent, isEuropeVisitor } from "@/lib/europeVisitor";

// Primary GA4 measurement ID for this property. The cookie name follows the
// pattern `_ga_<MEASUREMENT_ID without the G- prefix>`.
const GA4_MEASUREMENT_ID_SUFFIX = "M09E85VL9J";

export type GA4EventName =
  | "sign_up"
  | "signup_view"
  | "signup_start"
  | "signup_submit"
  | "signup_field_error"
  | "email_verification_sent"
  | "email_verification_complete"
  | "first_login"
  | "cta_click"
  | "generate_lead"
  | "form_thank_you_view"
  | "begin_checkout"
  | "purchase";

export interface GA4EventOptions {
  /** Optional explicit user_id (otherwise pulled from JWT server-side). */
  user_id?: string;
  /** Custom event params to send to GA4. */
  params?: Record<string, unknown>;
  /** GA4 user_properties payload. */
  user_properties?: Record<string, { value: unknown }>;
}

/** Read a cookie value by name (browser only). */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Extract GA4 client_id from the `_ga` cookie (format: GA1.1.<clientId>). */
export function getGA4ClientId(): string | undefined {
  const raw = readCookie("_ga");
  if (!raw) return undefined;
  const m = raw.match(/^GA\d\.\d\.(.+)$/);
  return m?.[1];
}

/** Extract GA4 session_id from the `_ga_<id>` cookie (format: GS1.1.<sessionId>.<...>). */
export function getGA4SessionId(): string | undefined {
  const raw = readCookie(`_ga_${GA4_MEASUREMENT_ID_SUFFIX}`);
  if (!raw) return undefined;
  const m = raw.match(/^GS\d\.\d\.([^.]+)/);
  return m?.[1];
}

export { isProdHost } from "@/lib/analyticsHost";

/**
 * True when gtag.js actually loaded (not ad-blocked / not prod-gated away).
 * gtag.js defines `window.google_tag_manager` once it executes.
 */
export function isGtagLive(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { google_tag_manager?: Record<string, unknown>; gtag?: unknown };
  // HubSpot's AW-968217216 also defines google_tag_manager without loading
  // our property, so only a G-M09E85VL9J entry counts as GA4 being live.
  return typeof w.gtag === "function" && !!w.google_tag_manager?.[`G-${GA4_MEASUREMENT_ID_SUFFIX}`];
}

/**
 * Relay an event server-side ONLY when the client-side gtag hit could not be
 * sent (gtag.js blocked). Prevents the same event being counted twice.
 */
export function relayIfGtagBlocked(
  event_name: GA4EventName,
  options: GA4EventOptions = {},
): void {
  if (isGtagLive()) return;
  // Europe without analytics consent never started gtag.js. That is not an
  // ad-block; relaying would send the hit without consent.
  if (isEuropeVisitor() && !hasStoredAnalyticsConsent()) return;
  // Loader is in flight: the gtag stub will flush when the script lands.
  // Relaying now would double-count the same CTA/lead. index.html must
  // clear __c2Ga4Loading when the loader settles (success or failure);
  // leaving it true after an ad-blocked fallback suppresses this relay
  // for the rest of the session.
  if (typeof window !== "undefined" && window.__c2Ga4Loading) return;
  void trackGA4Event(event_name, options);
}

/**
 * Fire a GA4 event via the Measurement Protocol relay.
 * Fire-and-forget — never throws to the caller.
 * No-ops off production hosts so preview/localhost traffic never pollutes GA4.
 */
export async function trackGA4Event(
  event_name: GA4EventName,
  options: GA4EventOptions = {},
): Promise<void> {
  try {
    // Production-only: preview (*.lovable.app), localhost and any other host
    // must never send hits to the live GA4 property.
    if (!isProdHost()) {
      if (typeof console !== "undefined") {
        console.info(`[ga4Tracking] skipped '${event_name}' on non-prod host`);
      }
      return;
    }

    // Europe without analytics consent: do not mint a Measurement Protocol
    // hit either. Direct callers (sign_up, begin_checkout, purchase) used to
    // bypass the relayIfGtagBlocked gate.
    if (isEuropeVisitor() && !hasStoredAnalyticsConsent()) return;

    const client_id = getGA4ClientId();
    const session_id = getGA4SessionId();

    // DebugView stays available on prod via the ga_debug flag.
    let debugMode = false;
    if (typeof window !== "undefined") {
      try {
        debugMode = window.localStorage?.getItem("ga_debug") === "1";
      } catch { /* no-op */ }
    }


    const params: Record<string, unknown> = {
      ...(session_id ? { session_id } : {}),
      ...(debugMode ? { debug_mode: 1 } : {}),
      ...(options.params ?? {}),
    };

    const { error } = await supabase.functions.invoke("track-signup-mp", {
      body: {
        event_name,
        client_id,
        ...(options.user_id ? { user_id: options.user_id } : {}),
        params,
        ...(options.user_properties ? { user_properties: options.user_properties } : {}),
      },
    });

    if (error) {
      console.warn(`[ga4Tracking] ${event_name} relay error`, error);
    }
  } catch (err) {
    console.warn(`[ga4Tracking] ${event_name} failed`, err);
  }
}
